import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LanguageAwareLink from "@/components/LanguageAwareLink";
import { Helmet } from "react-helmet-async";

const caseStudies = [
  {
    id: 1,
    title: "Digital Transformation for RiceGo F&B Chain",
    description: "How we helped a leading Vietnamese F&B brand with 10+ locations integrate AI solutions into their customer service and management systems.",
    image: "/lovable-uploads/fc6e8cca-180c-44eb-bdac-0f05fde126bf.png",
    results: [
      "35% increase in customer satisfaction",
      "42% reduction in order processing time",
      "28% boost in operational efficiency"
    ],
    industry: "Food & Beverage",
    slug: "ricego-digital-transformation"
  },
  {
    id: 2,
    title: "Digital and AI Transformation for Ngoc Minh Medical Center",
    description: "How we revolutionized patient care and streamlined operations at a leading medical center through AI-powered solutions.",
    image: "/clients/ngocminh image.png",
    results: [
      "40% increase in customer service efficiency",
      "30% boost in appointment bookings",
      "45% improvement in patient satisfaction ratings"
    ],
    industry: "Healthcare",
    slug: "ngocminh-digital-ai-transformation"
  }
  // Other case studies are temporarily hidden
];

const CaseStudies = () => {
  // Get unique industries for filtering
  const industries = ["All", ...new Set(caseStudies.map(study => study.industry))];
  
  return (
    <div className="min-h-screen bg-maitech-900 text-foreground">
      <Helmet>
        <title>MAI Tech Solutions | MAI Tech</title>
        <meta name="description" content="Explore our technology solutions that have transformed businesses and delivered measurable results." />
        <meta property="og:title" content="MAI Tech Solutions | MAI Tech" />
        <meta property="og:description" content="Explore our technology solutions that have transformed businesses." />
        <meta property="og:image" content="/lovable-uploads/fc6e8cca-180c-44eb-bdac-0f05fde126bf.png" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Navbar />
      <div className="pt-16 md:pt-20 pb-12 md:pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10 md:mb-16">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6 gradient-text">MAI Tech Solutions</h1>
            <p className="text-base md:text-lg text-gray-400 mb-6 md:mb-8">
              Explore our technology solutions that have transformed businesses and delivered measurable results
            </p>
          </div>

          {/* Industry filter chips - can be uncommented when more case studies are added */}
          {/* <div className="flex flex-wrap gap-2 justify-center mb-8">
            {industries.map((industry) => (
              <button
                key={industry}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                  industry === "All" 
                    ? "bg-primary text-white" 
                    : "bg-maitech-800 hover:bg-maitech-700 text-gray-300"
                }`}
              >
                {industry}
              </button>
            ))}
          </div> */}

          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 md:gap-8 max-w-7xl mx-auto">
            {caseStudies.map((study) => (
              <Card 
                key={study.id} 
                className="bg-maitech-800 border-primary/20 hover:border-primary transition-all duration-300 overflow-hidden hover:shadow-lg hover:shadow-primary/10 group"
              >
                <LanguageAwareLink to={`/case-study/${study.slug}`} className="no-underline">
                  <div className="h-56 md:h-64 overflow-hidden">
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </LanguageAwareLink>
                <CardContent className="p-6 md:p-8">
                  <div className="text-xs font-semibold text-primary/80 uppercase tracking-wide mb-3">
                    {study.industry}
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">{study.title}</h3>
                  <p className="text-gray-300 mb-5 text-sm md:text-base">{study.description}</p>
                  
                  <div className="mb-6 bg-maitech-900/50 p-4 rounded-lg">
                    <h4 className="text-sm md:text-base font-semibold text-white mb-3">Key Results:</h4>
                    <ul className="space-y-2">
                      {study.results.map((result, idx) => (
                        <li key={idx} className="text-sm text-gray-300 flex items-start">
                          <span className="text-primary mr-2 text-lg">•</span>
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <LanguageAwareLink to={`/case-study/${study.slug}`}>
                    <Button className="w-full justify-center">
                      Read full case study
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </LanguageAwareLink>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Add a contact CTA at the bottom */}
          <div className="mt-16 text-center p-8 bg-gradient-to-r from-maitech-800 to-maitech-900 rounded-xl max-w-5xl mx-auto border border-primary/20">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to Transform Your Business?</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how our AI and digital solutions can help your organization overcome challenges and drive growth.
            </p>
            <LanguageAwareLink to="/contact">
              <Button size="lg" className="px-8">
                Contact Us Today
              </Button>
            </LanguageAwareLink>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CaseStudies;
